import { Divider, Flex, List, Popover, Typography } from "antd";
import { cents, formatPrice } from "@/utilities";
import { t } from "i18next";
import { DiscountPrice, extractDescriptionDiscount } from "../discount-icon";

type SumaryProps = {
  sumaryItems?: any;
  shipmentItems?: any;
  interests?: number;
  additionalPayment?: number;
  discountAmount?: number;
  discountDescription?: string;
};

const { Title, Text, Paragraph } = Typography;

export const OrderSumary = ({
  sumaryItems = [],
  shipmentItems = [],
  interests = 0,
  additionalPayment = 0,
  discountAmount = 0,
  discountDescription = "",
}: SumaryProps) => {
  const enrichedSummaryItems = sumaryItems?.map((item: any) => {
    let relatedShipments = shipmentItems?.filter((shipment: any) =>
      shipment.items?.some(
        (shipmentItem: any) => shipmentItem.orderItemId === item.itemId,
      ),
    );

    // Fallback for service/virtual items that are not explicitly part of a shipment
    if ((!relatedShipments || relatedShipments.length === 0) && shipmentItems?.length > 0) {
      relatedShipments = [shipmentItems[0]];
    }

    return {
      ...item,
      relatedShipments,
    };
  });

  const N = (v: any, d = 0) => Number(v ?? d);

  const shipmentAsItems = shipmentItems.map((shipment: any) => {
    const orderItemId = shipment.items?.[0]?.orderItemId;

    return {
      isShipment: true,
      itemId: orderItemId,
      shipment,
    };
  });

  const unifiedSummaryItems = [...enrichedSummaryItems, ...shipmentAsItems];

  const { subtotalBaseExcl, subtotalFinalExcl, total, discountsExcl } =
    unifiedSummaryItems.reduce(
      (acc, it: any) => {
        if (it.isShipment) {
          acc.subtotalBaseExcl += N(it.shipment?.price);
          acc.subtotalFinalExcl += N(it.shipment?.price);
          acc.total += N(it.shipment?.priceInclTax);
        } else {
          const am = calcItemAmounts(it);
          acc.subtotalBaseExcl += am.rowBaseExcl;
          acc.subtotalFinalExcl += am.rowFinalExcl;
          acc.total += am.rowFinalIncl;
          acc.discountsExcl += am.rowDiscountExcl;
        }
        return acc;
      },
      { subtotalBaseExcl: 0, subtotalFinalExcl: 0, total: 0, discountsExcl: 0 },
    );

  function calcItemAmounts(item: any) {
    const N = (v: any, d = 0) => Number(v ?? d);
    const qty = Math.max(1, N(item?.qtyOrdered ?? item?.qty, 1));
    const taxPct = N(item?.taxPercent, 0);
    const taxFactor = 1 + taxPct / 100;

    // base de fila SIN IVA (pre-descuento)
    const rowBaseExcl =
      item?.rowTotal != null
        ? N(item.rowTotal)
        : (N(item.rowTotalInclTax ?? item.priceInclTax) / taxFactor) * qty;

    // descuento CON IVA desde description
    const { amount: descDiscountIncl } = extractDescriptionDiscount(
      item.description,
    );

    // lo pasamos a EXCL:
    const rowDiscExcl =
      descDiscountIncl > 0
        ? Math.max(
          0,
          Math.round((descDiscountIncl / taxFactor + Number.EPSILON) * 100) /
          100,
        )
        : 0;

    const rowFinalExcl = Math.max(0, rowBaseExcl - rowDiscExcl);
    const rowFinalIncl = rowFinalExcl * taxFactor;

    const unitFinalIncl = rowFinalIncl / qty;
    const rowTax = rowFinalIncl - rowFinalExcl;

    return {
      qty,
      taxPct,
      rowBaseExcl,
      rowFinalExcl,
      rowFinalIncl,
      unitFinalIncl,
      rowTax,
      rowDiscountExcl: rowDiscExcl,
    };
  }

  const orderDiscountIncl = Math.abs(N(discountAmount));
  const roundCents = (n: number) => Math.ceil((n + Number.EPSILON) * 100) / 100;

  // Suma de descuentos CON IVA desde description a nivel ítem:
  const itemsDescDiscountIncl = sumaryItems.reduce(
    (acc: number, it: any) => acc + parseDescriptionDiscount(it?.description),
    0,
  );

  // Magento prorratea el order discount sobre todos los items (incluyendo el
  // virtual de envío) y guarda la cuota en cada `description.discount_amount`.
  // El backend de la API filtra los virtuales (`orders.service.ts`: `items.is_virtual = 0`),
  // así que la cuota del envío no llega como item — pero `discountAmount` SÍ
  // refleja el descuento total. Si restáramos `discountAmount` completo,
  // doble-contaríamos la cuota que ya está aplicada a los items reales.
  // Restamos sólo la diferencia (= cuota prorrateada al envío virtual).
  const hasStandaloneOrderDisc =
    orderDiscountIncl > 0 &&
    Math.abs(orderDiscountIncl - itemsDescDiscountIncl) > 0.05;
  const remainingOrderDiscIncl = hasStandaloneOrderDisc
    ? Math.max(0, orderDiscountIncl - itemsDescDiscountIncl)
    : 0;

  // Total CON IVA:
  const summaryTotal = roundCents(
    total +
    N(interests) +
    N(additionalPayment) -
    remainingOrderDiscIncl,
  );

  const KNOWN_RATES = [27, 21, 10.5] as const;

  function parseDescriptionDiscount(desc: unknown): number {
    if (!desc) return 0;
    try {
      const parsed = typeof desc === "string" ? JSON.parse(desc) : desc;
      const v = Number(parsed?.discount_amount);
      return Number.isFinite(v) ? Math.max(0, v) : 0;
    } catch {
      return 0;
    }
  }

  function buildTaxesMap(rows: any[]) {
    const map = new Map<number, number>(); // percent => amount

    for (const it of rows || []) {
      // sólo items (los shipments no traen taxPercent)
      const percent = Number(it.taxPercent ?? 0);
      if (!Number.isFinite(percent) || percent <= 0) continue;

      const qty = Number(it.qtyOrdered ?? it.qty ?? 1) || 1;
      const taxFactor = 1 + percent / 100;

      // base EXCL (pre-descuento)
      const baseExcl = Number.isFinite(Number(it.rowTotal))
        ? Number(it.rowTotal)
        : (Number(it.rowTotalInclTax ?? it.priceInclTax ?? 0) / taxFactor) *
        qty;

      // descuento desde description (VIENE CON IVA) -> llevar a EXCL
      let discountExcl = 0;
      try {
        const parsed =
          typeof it.description === "string"
            ? JSON.parse(it.description)
            : it.description;
        const descIncl = Number(parsed?.discount_amount ?? 0) || 0;
        if (descIncl > 0) discountExcl = descIncl / taxFactor;
      } catch {
        // ignore
      }
      // fallback legacy EXCL
      if (discountExcl === 0)
        discountExcl = Number(it.discountAmount ?? 0) || 0;

      const adjustedBase = Math.max(0, baseExcl - discountExcl);
      const taxAmount = adjustedBase * (percent / 100);

      map.set(percent, (map.get(percent) || 0) + taxAmount);
    }

    // objeto ordenado por % asc
    return Object.fromEntries(
      Array.from(map.entries()).sort(([a], [b]) => a - b),
    );
  }

  const snapRate = (p?: number) => {
    if (!p || !isFinite(p)) return undefined;
    // acercá a las alícuotas típicas si está “cerca”
    for (const r of KNOWN_RATES) {
      if (Math.abs(p - r) < 0.3) return r;
    }
    // si no calza, redondeá a 2 decimales
    return Math.round(p * 100) / 100;
  };

  const taxesMap = buildTaxesMap(unifiedSummaryItems);

  function prorrateOrderDiscountExcl(
    orderDiscountIncl: number,
    items: Array<{ taxPercent: number; rowTotalExcl: number }>,
  ) {
    if (!orderDiscountIncl) return [];
    // peso por total CON IVA de cada item
    const rows = items.map((it) => {
      const taxFactor = 1 + Number(it.taxPercent || 0) / 100;
      const rowIncl = it.rowTotalExcl * taxFactor;
      return { ...it, rowIncl, taxFactor };
    });
    const totalIncl = rows.reduce((a, r) => a + r.rowIncl, 0) || 1;

    return rows.map((r) => {
      const partIncl = (orderDiscountIncl * r.rowIncl) / totalIncl;
      const partExcl = partIncl / r.taxFactor;
      return { taxPercent: r.taxPercent, discountExcl: cents(partExcl) };
    });
  }

  const orderDiscountExcl = hasStandaloneOrderDisc
    ? prorrateOrderDiscountExcl(
      orderDiscountIncl,
      sumaryItems.map((it: any) => ({
        taxPercent: N(it?.taxPercent, 0),
        rowTotalExcl: N(it?.rowTotal, 0),
      })),
    ).reduce((a: number, r: any) => a + N(r.discountExcl), 0)
    : 0;

  const totalDiscountsExcl = cents(discountsExcl + orderDiscountExcl);

  const baseExclAfterDiscounts = cents(subtotalBaseExcl - totalDiscountsExcl);

  const taxFromTotals = cents(
    summaryTotal -
    baseExclAfterDiscounts -
    N(interests) -
    N(additionalPayment),
  );


  return (
    <List
      className="summary-list"
      itemLayout="vertical"
      size="large"
      dataSource={unifiedSummaryItems}
      header={
        <Flex
          align="center"
          justify="center"
          style={{
            backgroundColor: "#666",
            borderRadius: "8px 8px 0 0",
            padding: "8px 16px",
          }}
        >
          <Text style={{ color: "white", flex: 1, whiteSpace: "nowrap" }} strong>
            {t("ordersummary.table.products")}
          </Text>
          <Divider type="vertical" style={{ borderColor: "white" }} />
          <Flex justify="center" gap={80} style={{ flex: 1 }}>
            <Text style={{ color: "white", whiteSpace: "nowrap" }} strong>
              {t("ordersummary.table.unitprince")}
              <Divider type="vertical" style={{ borderColor: "white" }} />
            </Text>
          </Flex>
          <Flex align="center" gap={16} style={{ flex: 1 }}>
            <Flex style={{ flex: 1 }} justify="end">
              <Text style={{ color: "white", whiteSpace: "nowrap" }} strong>
                {t("ordersummary.table.quantity")}
                <Divider type="vertical" style={{ borderColor: "white" }} />
              </Text>
            </Flex>
            <Flex style={{ width: 148, flexShrink: 0 }} justify="end">
              <Text style={{ color: "white", whiteSpace: "nowrap" }} strong>
                $ {t("orders.label.balance")}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      }
      footer={
        <Flex justify="end">
          <Flex gap={8} vertical style={{ width: 438, padding: 4 }}>
            <Flex
              vertical
              style={{
                padding: "4px 8px",
                borderRadius: 6,
                border: "1px solid #F0F0F0",
              }}
            >
              {/* Subtotal */}
              <Flex
                align="center"
                justify="space-between"
                style={{ width: "100%" }}
              >
                <Text>Subtotal (sin impuestos)</Text>
                <span>{formatPrice(subtotalBaseExcl)}</span>
              </Flex>
              {/* Descuentos automaticos */}
              {hasStandaloneOrderDisc && (
                <Flex
                  align="center"
                  justify="space-between"
                  gap={56}
                  style={{ width: "100%" }}
                >
                  <Text>{discountDescription || "Descuento de orden"}</Text>
                  <span>{formatPrice(orderDiscountExcl)}</span>
                </Flex>
              )}
              {/* Descuentos */}
              <Flex
                align="center"
                justify="space-between"
                style={{ width: "100%" }}
              >
                <Text>Descuentos (sin impuestos)</Text>
                <span>{formatPrice(totalDiscountsExcl)}</span>
              </Flex>

              {/* Impuestos agrupados por % */}
              <Flex
                align="center"
                justify="space-between"
                gap={56}
                style={{ width: "100%" }}
              >
                <Flex gap={12}>
                  <Text>Impuestos</Text>
                </Flex>
                <span>{formatPrice(taxFromTotals)}</span>
              </Flex>

              {/* Percepciones */}
              <Flex
                align="center"
                justify="space-between"
                gap={56}
                style={{ width: "100%" }}
              >
                <Text>Percepciones</Text>
                <span>{formatPrice(additionalPayment)}</span>
              </Flex>
              {/* Intereses */}
              <Flex
                align="center"
                justify="space-between"
                gap={56}
                style={{ width: "100%" }}
              >
                <Text>Intereses</Text>
                <span>{formatPrice(interests || 0)}</span>
              </Flex>
            </Flex>

            {/* total / subtotal  */}
            <Flex
              align="end"
              justify="center"
              vertical
              style={{
                backgroundColor: "rgba(218, 232, 224, 1)",
                padding: 8,
                borderRadius: 6,
              }}
            >
              <Flex
                align="center"
                justify="end"
                gap={146}
                style={{ width: "100%" }}
              >
                <Flex vertical align="end">
                  <Title style={{ margin: 0 }} level={4}>
                    Total
                  </Title>
                  <span style={{ fontSize: 12 }}>Sin impuestos</span>
                </Flex>

                <Flex vertical align="end">
                  <Title style={{ margin: 0 }} level={4}>
                    {formatPrice(summaryTotal)}
                  </Title>
                  <span style={{ fontSize: 12 }}>
                    {formatPrice(baseExclAfterDiscounts)}
                  </span>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      }
      renderItem={(item: any, index) => {
        const linkedProduct = item?.linkedProduct;
        const am = calcItemAmounts(item);

        // Base con IVA incluido proveniente del item
        const baseIncl = Number(item.rowTotalInclTax ?? item.priceInclTax ?? 0);

        // Descuento desde el nuevo JSON de "description"
        const { amount: descDiscountAmount, label: descLabel } =
          extractDescriptionDiscount(item.description);

        // Precio final con descuento aplicado (no negativo)
        const displayRowFinalIncl = Math.max(0, baseIncl - descDiscountAmount);

        // 1. Total entregado para este ítem (solo shipments delivered)
        const totalDeliveredQty =
          item.relatedShipments
            ?.filter((shipment: any) => shipment.status === "delivered")
            .reduce((acc: number, shipment: any) => {
              const matched = shipment.items?.find(
                (shipmentItem: any) => shipmentItem.orderItemId === item.itemId,
              );
              if (matched) return acc + (matched?.qty || 0);
              return acc + (Number(item.qtyOrdered) || 0);
            }, 0) || 0;

        if (item.isShipment) {
          const OPEN_STATUSES = new Set([
            "pending",
            "ready_to_deliver",
            "in_transit",
          ]);
          const shipment = item.shipment;

          const isOpenShipment = OPEN_STATUSES.has(shipment.status);

          // suma qty del shipment (puede tener varios items)
          const shipmentQty = (shipment.items || []).reduce(
            (a: number, si: any) => a + (Number(si.qty) || 0),
            0,
          );

          // Sólo hay “pendiente” si el envio está ABIERTO
          const pendingAmount = isOpenShipment
            ? Number(shipment.priceInclTax) || 0
            : 0;

          const pendingShipmentQty = shipmentQty - totalDeliveredQty;
          const priceInclTax = Number(shipment.priceInclTax);
          const price = Number(shipment.price);
          const isDelivered = shipment.status === "delivered";

          return (
            <List.Item
              key={`shipment-${shipment.shipmentId}`}
              style={{ padding: 0 }}
            >
              <Flex
                align="baseline"
                justify="center"
                gap={8}
                style={{
                  borderLeft: "1px solid #F0F0F0",
                  borderRight: "1px solid #F0F0F0",
                  borderBottom: "1px solid #F0F0F0",
                  padding: 8,
                  overflowY: "auto",
                  height: "100%",
                }}
              >
                {/* products */}
                <Flex gap={16} style={{ flex: 1 }}>
                  <Text style={{ width: 39 }} strong>{`${index + 1 >= 10 ? `0${index + 1}` : `00${index + 1}`
                    }`}</Text>
                  <Flex vertical style={{ width: 400 }} gap={2}>
                    <Text strong>{shipment.metadata?.name}</Text>
                    <Text italic>{shipment.metadata?.shippingTypeLabel}</Text>
                  </Flex>
                </Flex>

                {/* unit price */}
                <Flex style={{ flex: 1, marginRight: 22 }} justify="center">
                  <DiscountPrice shipment={shipment} />
                </Flex>

                {/* qty & balance */}
                <Flex vertical gap={0} style={{ width: 430, flexShrink: 0, border: "1px solid #F0F0F0", borderRadius: 6 }}>
                  <div style={{ padding: "0 8px" }}>
                    <Flex align="center" gap={24} style={{ width: "100%" }}>
                      <Flex style={{ flex: 1 }} justify="end">
                        <Flex vertical align="end">
                          <Flex align="center" gap={8}>
                            <Title style={{ margin: 0, color: "#2B8058", fontSize: 16 }} level={4}>
                              {t("orders.label.orderinvoice")}
                            </Title>
                            <Title style={{ margin: 0, fontSize: 16 }} level={4}>
                              1
                            </Title>
                          </Flex>
                          <Flex gap={8}>
                            <span style={{ fontSize: 12 }}>Sin impuestos</span>
                            <span style={{ fontSize: 12 }}>{item?.taxPercent || 21}%</span>
                          </Flex>
                        </Flex>
                      </Flex>
                      <Flex vertical align="end" style={{ width: 140, flexShrink: 0 }}>
                        <Title style={{ margin: 0, fontSize: 16 }} level={4}>
                          {formatPrice(priceInclTax)}
                        </Title>
                        <span style={{ fontSize: 12 }}>{formatPrice(price)}</span>
                      </Flex>
                    </Flex>
                  </div>

                  <Divider style={{ margin: 0 }} />

                  <div className="summary-delivery-underline" style={{ padding: "0 8px" }}>
                    <Flex align="center" gap={24} style={{ width: "100%" }}>
                      <Flex style={{ flex: 1 }} justify="end" gap={8}>
                        <Text strong italic style={{ color: "#FF8F1F" }}>
                          {t("orders-label.pending")}
                        </Text>
                        <Text strong italic style={{ color: "#FF8F1F" }}>
                          {isOpenShipment ? 1 : 0}
                        </Text>
                      </Flex>
                      <Flex style={{ width: 140, flexShrink: 0 }} justify="end">
                        <Text strong italic style={{ color: "#FF8F1F" }}>
                          {formatPrice(pendingAmount)}
                        </Text>
                      </Flex>
                    </Flex>
                  </div>
                </Flex>
              </Flex>
            </List.Item>
          );
        }

        const OPEN_STATUSES = new Set([
          "pending",
          "ready_to_deliver",
          "in_transit",
        ]);

        const qtyOf = (s: any) => {
          const si = s?.items?.find(
            (it: any) => it.orderItemId === item.itemId,
          );
          if (si) return Number(si?.qty) || 0;
          return Number(item.qtyOrdered) || 0;
        };

        // Qty asignada a envíos ABIERTOS ⇒ pendiente real
        const pendingQty =
          (item.relatedShipments || [])
            .filter((s: any) => OPEN_STATUSES.has(s.status))
            .reduce((acc: number, s: any) => acc + qtyOf(s), 0) || 0;

        // Monto pendiente = unitario final CON IVA * qty pendiente
        const pendingBalance = pendingQty * am.unitFinalIncl;

        return (
          <>
            <List.Item key={index} style={{ padding: 0 }}>
              <Flex
                align="baseline"
                justify="center"
                gap={8}
                style={{
                  borderLeft: "1px solid #F0F0F0",
                  borderRight: "1px solid #F0F0F0",
                  borderBottom: "1px solid #F0F0F0",
                  padding: 8,
                  overflowY: "auto",
                  height: "100%",
                }}
              >
                <Flex gap={16} style={{ flex: 1 }}>
                  <Text style={{ width: 39 }} strong>{`${index + 1 >= 10 ? `0${index + 1}` : `00${index + 1}`
                    }`}</Text>
                  <Flex vertical style={{ width: 400 }} gap={2}>
                    <Text strong>{linkedProduct?.name}</Text>
                    <Text italic>
                      {linkedProduct?.variations?.[0]?.mainCatalog?.name}
                    </Text>
                    <Text style={{ fontSize: 12 }}>Seller SKU {linkedProduct?.sku}</Text>
                  </Flex>
                </Flex>

                {/* unit price */}
                <Flex style={{ flex: 1, marginRight: 22 }} justify="center">
                  <DiscountPrice item={item} />
                </Flex>

                {/* qty & balance */}
                <Flex vertical gap={0} style={{ width: 430, flexShrink: 0, border: "1px solid #F0F0F0", borderRadius: 6 }}>
                  <div style={{ padding: "0 8px" }}>
                    <Flex align="center" gap={24} style={{ width: "100%" }}>
                      <Flex style={{ flex: 1 }} justify="end">
                        <Flex vertical align="end">
                          <Flex align="center" gap={8}>
                            <Title style={{ margin: 0, color: "#2B8058", fontSize: 16 }} level={4}>
                              {t("orders.label.orderinvoice")}
                            </Title>
                            <Title style={{ margin: 0, fontSize: 16 }} level={4}>
                              {item?.qtyOrdered}
                            </Title>
                          </Flex>
                          <Flex gap={8}>
                            <span style={{ fontSize: 12 }}>Sin impuestos</span>
                            <span style={{ fontSize: 12 }}>{item?.taxPercent}%</span>
                          </Flex>
                        </Flex>
                      </Flex>
                      <Flex vertical align="end" style={{ width: 140, flexShrink: 0 }}>
                        <Title style={{ margin: 0, fontSize: 16 }} level={4}>
                          {formatPrice(displayRowFinalIncl)}
                        </Title>
                        <span style={{ fontSize: 12 }}>{formatPrice(displayRowFinalIncl / 1.21)}</span>
                      </Flex>
                    </Flex>
                  </div>

                  <Divider style={{ margin: 0 }} />

                  <div className="summary-delivery-underline" style={{ padding: "0 8px" }}>
                    <Flex vertical gap={8}>
                      {item?.relatedShipments?.map((shipment: any, i: any) => {
                        const shipmentItem = shipment.items?.find(
                          (it: any) => it.orderItemId === item.itemId,
                        );
                        const isDelivered = shipment.status === "delivered";
                        const pendingShipmentQty = isDelivered
                          ? 0
                          : shipmentItem ? (Number(shipmentItem.qty) || 0) : (Number(item.qtyOrdered) || 0);
                        const unitFinalIncl = am.unitFinalIncl;
                        const lineAmount = unitFinalIncl * pendingShipmentQty;

                        return (
                          <Flex key={i} align="center" gap={24} style={{ width: "100%" }}>
                            <Flex style={{ flex: 1 }} justify="end" gap={8}>
                              <Paragraph
                                strong
                                style={{ color: "#2B8058", maxWidth: 150, margin: 0 }}
                                ellipsis={{ tooltip: shipment.metadata?.name }}
                              >
                                {shipment.metadata?.name || "Partial cancelation"}
                              </Paragraph>
                              <Text>-{pendingShipmentQty}</Text>
                            </Flex>
                            <Flex style={{ width: 140, flexShrink: 0 }} justify="end">
                              <Text>-{formatPrice(lineAmount)}</Text>
                            </Flex>
                          </Flex>
                        );
                      })}
                    </Flex>
                  </div>

                  <Divider style={{ margin: 0 }} />

                  <div className="summary-delivery-underline" style={{ padding: "0 8px" }}>
                    <Flex align="center" gap={24} style={{ width: "100%" }}>
                      <Flex style={{ flex: 1 }} justify="end" gap={8}>
                        <Text strong italic style={{ color: "#FF8F1F" }}>
                          {t("orders-label.pending")}
                        </Text>
                        <Text strong italic style={{ color: "#FF8F1F" }}>
                          {pendingQty}
                        </Text>
                      </Flex>
                      <Flex style={{ width: 140, flexShrink: 0 }} justify="end">
                        <Text strong italic style={{ color: "#FF8F1F" }}>
                          {formatPrice(pendingBalance)}
                        </Text>
                      </Flex>
                    </Flex>
                  </div>
                </Flex>
              </Flex>
            </List.Item>
          </>
        );
      }}
    />
  );
};
